import { zodResolver } from '@hookform/resolvers/zod';
import { FC, ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  Control,
  FieldError,
  FieldValues,
  useForm,
  SubmitHandler,
  DefaultValues,
} from 'react-hook-form';
import { ZodType } from 'zod';
import 'primeicons/primeicons.css';

import Button from '@/components/controls/Button';
import CustomEntityFormField, {
  EntityFormFieldSpecification,
} from '@/components/controls/CustomEntityFormField';

export type EntityFormMode = 'view' | 'edit' | 'create';

export interface EntityFormProps<T extends FieldValues> {
  schema: ZodType<T>;
  entityData?: T;
  initialMode: EntityFormMode;
  fields: EntityFormFieldSpecification[];
  onSubmit: (data: T) => Promise<T | void> | T | void;
  onCancel: () => void;
  title?: string | ReactNode;
  submitButtonText?: string;
  submitButtonDisabled?: boolean;
  isLoading?: boolean;
}

interface EntityFormActionsProps {
  isLoading?: boolean;
  onCancel: () => void;
  isFormInvalid?: boolean;
  submitButtonText?: string;
  submitButtonDisabled?: boolean;
}

const EntityFormActions: FC<EntityFormActionsProps> = ({
  isLoading = false,
  onCancel,
  isFormInvalid = false,
  submitButtonText,
  submitButtonDisabled = false,
}) => {
  return (
    <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
      <Button
        buttonStyle="
          justify-center items-center px-9 py-1.5 text-base font-semibold text-white
          whitespace-nowrap bg-honoursOrange rounded-xl w-full sm:w-auto
          transition-opacity duration-150
          hover:opacity-90 disabled:opacity-60
        "
        text={submitButtonText}
        buttonType={{ type: 'submit' }}
        disabled={isLoading || isFormInvalid || submitButtonDisabled}
        hasImage={false}
      />

      <Button
        buttonStyle="
          justify-center items-center px-9 py-1.5 text-base font-semibold
          border border-honoursOrange text-honoursOrange whitespace-nowrap
          bg-white rounded-xl w-full sm:w-auto
          transition-opacity duration-150
          hover:opacity-80 disabled:opacity-60
        "
        buttonType={{ type: 'button' }}
        text="Cancel"
        onClick={onCancel}
        disabled={isLoading}
        hasImage={false}
      />
    </div>
  );
};

function CustomEntityForm<T extends FieldValues>({
  schema,
  entityData,
  initialMode,
  fields,
  onSubmit,
  onCancel,
  title,
  submitButtonText = 'Submit',
  isLoading = false,
  submitButtonDisabled = false,
}: EntityFormProps<T>): ReactElement {
  const [currentMode, setCurrentMode] = useState<EntityFormMode>(initialMode);
  const [localEntityData, setLocalEntityData] = useState<T | undefined>(
    entityData
  );

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    setValue,
    formState: { errors, isDirty, touchedFields },
  } = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues: (localEntityData || {}) as DefaultValues<T>,
    mode: 'onChange',
  });

  const hasErrors = Object.keys(errors).length > 0;
  const hasInteracted = Object.keys(touchedFields).length > 0 || isDirty;
  const isFormInvalid = hasInteracted && hasErrors;

  useEffect(() => {
    if (localEntityData) {
      reset(localEntityData as DefaultValues<T>);
    } else {
      reset({} as DefaultValues<T>);
    }
  }, [reset, localEntityData]);

  useEffect(() => {
    setCurrentMode(initialMode);
  }, [initialMode]);

  const toggleMode = (): void => {
    const newMode = currentMode === 'view' ? 'edit' : 'view';
    setCurrentMode(newMode);
  };

  const processSubmit: SubmitHandler<T> = async (data: T) => {
    const result = await onSubmit(data);
    const finalData = result || data;

    if (currentMode === 'edit') {
      setLocalEntityData(finalData);
      setCurrentMode('view');
    }
  };

  const isViewMode = currentMode === 'view';

  return (
    <form onSubmit={handleSubmit(processSubmit)}>
      {title ? (
        <div className="text-h4 mb-4 font-medium text-primary-100 flex items-center">
          <span className="font-semibold">{title}</span>
          {isViewMode ? (
            <Button
              text=""
              buttonType={{ type: 'button' }}
              onClick={toggleMode}
              buttonStyle="ml-3 cursor-pointer"
              disabled={isLoading}
              pointer={
                <i className="pi pi-pen-to-square text-xl self-center" />
              }
              hasImage
              tooltipText="Edit"
            />
          ) : null}
        </div>
      ) : null}

      <div className="space-y-4">
        {fields.map((field) => {
          const isDisabled =
            isViewMode || (currentMode === 'edit' && field.disabledInEdit);

          return (
            <CustomEntityFormField
              key={field.name}
              field={field}
              control={control as Control<any>}
              error={errors[field.name] as FieldError}
              disabled={isDisabled}
              clearErrors={clearErrors}
              setValue={setValue}
            />
          );
        })}
      </div>

      {currentMode !== 'view' ? (
        <EntityFormActions
          isLoading={isLoading}
          submitButtonText={submitButtonText}
          submitButtonDisabled={submitButtonDisabled && !isDirty}
          isFormInvalid={isFormInvalid}
          onCancel={() => {
            reset((localEntityData || {}) as DefaultValues<T>);

            if (currentMode === 'edit') {
              toggleMode();
            } else {
              onCancel();
            }
          }}
        />
      ) : null}
    </form>
  );
}

export default CustomEntityForm;
